import React, { useEffect, useRef, useState } from 'react';
import './ScreenReaderText.css';

/**
 * ScreenReaderText component for screen reader accessibility
 * Based on ArbiWin design system and WCAG guidelines
 *
 * @param {Object} props - ScreenReaderText properties
 * @param {React.ReactNode} props.children - Text content for screen readers
 * @param {string} props.as - HTML element to render as
 * @param {boolean} props.focusable - Whether the element can receive focus
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} ScreenReaderText component
 */
const ScreenReaderText = function ScreenReaderText({
  children,
  as = 'span',
  focusable = false,
  className = '',
  testId
}) {

  const screenReaderClasses = [
    'arbiwin-sr-only',
    focusable ? 'arbiwin-sr-only--focusable' : '',
    className
  ].filter(Boolean).join(' ');

  const Element = as;
  const tabIndex = focusable ? 0 : undefined;

  return (
    <Element
      className={screenReaderClasses}
      tabIndex={tabIndex}
      data-testid={testId || 'arbiwin-screen-reader-text'}
    >
      {children}
    </Element>
  );
};

/**
 * LiveAnnouncement component for dynamic screen reader announcements
 *
 * @param {Object} props - LiveAnnouncement properties
 * @param {string} props.message - Message to announce
 * @param {string} props.priority - Announcement priority: 'polite', 'assertive'
 * @param {boolean} props.atomic - Whether to read the entire content
 * @param {number} props.delay - Delay before announcement in ms
 * @param {Function} props.onAnnounced - Callback when announcement is made
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} LiveAnnouncement component
 */
const LiveAnnouncement = function LiveAnnouncement({
  message,
  priority = 'polite',
  atomic = true,
  delay = 100,
  onAnnounced,
  className = ''
}) {

  const [currentMessage, setCurrentMessage] = useState('');
  const timeoutRef = useRef(null);

  useEffect(function() {
    if (!message) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set the message after a delay to ensure screen readers pick it up
    timeoutRef.current = setTimeout(function() {
      setCurrentMessage(message);
      
      if (onAnnounced) {
        onAnnounced(message);
      }

      // Clear the message after it's been announced
      setTimeout(function() {
        setCurrentMessage('');
      }, 1000);
    }, delay);

    return function() {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [message, delay, onAnnounced]);

  const announcementClasses = [
    'arbiwin-live-announcement',
    'arbiwin-sr-only',
    className
  ].filter(Boolean).join(' ');

  return (
    <div
      className={announcementClasses}
      aria-live={priority}
      aria-atomic={atomic}
      role="status"
    >
      {currentMessage}
    </div>
  );
};

/**
 * DescriptiveText component for additional context
 *
 * @param {Object} props - DescriptiveText properties
 * @param {React.ReactNode} props.children - Descriptive text content
 * @param {string} props.for - ID of the element this describes
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} DescriptiveText component
 */
const DescriptiveText = function DescriptiveText({
  children,
  for: htmlFor,
  className = ''
}) {

  const descriptionId = 'description-' + (htmlFor || Date.now());

  useEffect(function() {
    if (htmlFor) {
      const targetElement = document.getElementById(htmlFor);
      if (targetElement) {
        const existingDescribedBy = targetElement.getAttribute('aria-describedby');
        const newDescribedBy = existingDescribedBy 
          ? existingDescribedBy + ' ' + descriptionId
          : descriptionId;
        
        targetElement.setAttribute('aria-describedby', newDescribedBy);

        return function() {
          const currentDescribedBy = targetElement.getAttribute('aria-describedby');
          if (currentDescribedBy) {
            const updatedDescribedBy = currentDescribedBy
              .split(' ')
              .filter(function(id) { return id !== descriptionId; })
              .join(' ');
            
            if (updatedDescribedBy) {
              targetElement.setAttribute('aria-describedby', updatedDescribedBy);
            } else {
              targetElement.removeAttribute('aria-describedby');
            }
          }
        };
      }
    }
  }, [htmlFor, descriptionId]);

  return (
    <ScreenReaderText
      as="div"
      className={'arbiwin-descriptive-text ' + className}
    >
      <span id={descriptionId}>
        {children}
      </span>
    </ScreenReaderText>
  );
};

/**
 * InstructionalText component for usage instructions
 *
 * @param {Object} props - InstructionalText properties
 * @param {React.ReactNode} props.children - Instructional text content
 * @param {string} props.for - ID of the element this instructs
 * @param {string} props.type - Type of instruction: 'usage', 'format', 'shortcut'
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} InstructionalText component
 */
const InstructionalText = function InstructionalText({
  children,
  for: htmlFor,
  type = 'usage',
  className = ''
}) {

  const instructionId = 'instruction-' + type + '-' + (htmlFor || Date.now());

  const instructionClasses = [
    'arbiwin-instructional-text',
    'arbiwin-instructional-text--' + type,
    className
  ].filter(Boolean).join(' ');

  useEffect(function() {
    if (htmlFor) {
      const targetElement = document.getElementById(htmlFor);
      if (targetElement) {
        const existingDescribedBy = targetElement.getAttribute('aria-describedby');
        const newDescribedBy = existingDescribedBy 
          ? existingDescribedBy + ' ' + instructionId
          : instructionId;
        
        targetElement.setAttribute('aria-describedby', newDescribedBy);

        return function() {
          const currentDescribedBy = targetElement.getAttribute('aria-describedby');
          if (currentDescribedBy) {
            const updatedDescribedBy = currentDescribedBy
              .split(' ')
              .filter(function(id) { return id !== instructionId; })
              .join(' ');
            
            if (updatedDescribedBy) {
              targetElement.setAttribute('aria-describedby', updatedDescribedBy);
            } else {
              targetElement.removeAttribute('aria-describedby');
            }
          }
        };
      }
    }
  }, [htmlFor, instructionId]);

  return (
    <ScreenReaderText
      as="div"
      className={instructionClasses}
    >
      <span id={instructionId}>
        {children}
      </span>
    </ScreenReaderText>
  );
};

/**
 * StatusText component for dynamic status updates
 *
 * @param {Object} props - StatusText properties
 * @param {string} props.status - Current status
 * @param {string} props.previousStatus - Previous status for comparison
 * @param {Function} props.formatStatus - Function to format status text
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} StatusText component
 */
const StatusText = function StatusText({
  status,
  previousStatus,
  formatStatus,
  className = ''
}) {

  const [announcement, setAnnouncement] = useState('');

  useEffect(function() {
    if (status !== previousStatus && status) {
      const statusText = formatStatus ? formatStatus(status, previousStatus) : status;
      setAnnouncement(statusText);
    }
  }, [status, previousStatus, formatStatus]);

  return (
    <LiveAnnouncement
      message={announcement}
      priority="polite"
      className={'arbiwin-status-text ' + className}
    />
  );
};

/**
 * ProgressText component for progress announcements
 *
 * @param {Object} props - ProgressText properties
 * @param {number} props.current - Current progress value
 * @param {number} props.total - Total progress value
 * @param {string} props.unit - Progress unit (e.g., 'percent', 'items')
 * @param {Function} props.formatProgress - Function to format progress text
 * @param {number} props.announceInterval - Interval for progress announcements
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ProgressText component
 */
const ProgressText = function ProgressText({
  current,
  total,
  unit = 'percent',
  formatProgress,
  announceInterval = 10,
  className = ''
}) {

  const [lastAnnounced, setLastAnnounced] = useState(0);
  const [announcement, setAnnouncement] = useState('');

  useEffect(function() {
    const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
    
    // Only announce at intervals or when complete
    if (
      percentage === 100 ||
      percentage === 0 ||
      percentage - lastAnnounced >= announceInterval
    ) {
      const progressText = formatProgress 
        ? formatProgress(current, total, percentage, unit)
        : percentage + '% завършено';
      
      setAnnouncement(progressText);
      setLastAnnounced(percentage);
    }
  }, [current, total, unit, formatProgress, announceInterval, lastAnnounced]);

  return (
    <LiveAnnouncement
      message={announcement}
      priority="polite"
      className={'arbiwin-progress-text ' + className}
    />
  );
};

/**
 * useScreenReaderAnnouncements hook for programmatic announcements
 *
 * @returns {Object} Screen reader announcement utilities
 */
const useScreenReaderAnnouncements = function() {
  const [announcements] = useState([]);
  const announcementRef = useRef(null);

  const announce = function(message, priority = 'polite', delay = 100) {
    if (!message) return;

    // Create temporary announcement element
    const announcementElement = document.createElement('div');
    announcementElement.setAttribute('aria-live', priority);
    announcementElement.setAttribute('aria-atomic', 'true');
    announcementElement.className = 'arbiwin-sr-only';
    announcementElement.style.position = 'absolute';
    announcementElement.style.left = '-10000px';
    
    document.body.appendChild(announcementElement);

    setTimeout(function() {
      announcementElement.textContent = message;
      
      // Remove after announcement
      setTimeout(function() {
        if (document.body.contains(announcementElement)) {
          document.body.removeChild(announcementElement);
        }
      }, 1000);
    }, delay);
  };

  const announcePolite = function(message, delay = 100) {
    announce(message, 'polite', delay);
  };

  const announceAssertive = function(message, delay = 100) {
    announce(message, 'assertive', delay);
  };

  const announceStatus = function(status, delay = 100) {
    announce('Статус: ' + status, 'polite', delay);
  };

  const announceError = function(error, delay = 100) {
    announce('Грешка: ' + error, 'assertive', delay);
  };

  const announceSuccess = function(message, delay = 100) {
    announce('Успех: ' + message, 'polite', delay);
  };

  return {
    announce,
    announcePolite,
    announceAssertive,
    announceStatus,
    announceError,
    announceSuccess
  };
};

ScreenReaderText.displayName = 'ScreenReaderText';
LiveAnnouncement.displayName = 'LiveAnnouncement';
DescriptiveText.displayName = 'DescriptiveText';
InstructionalText.displayName = 'InstructionalText';
StatusText.displayName = 'StatusText';
ProgressText.displayName = 'ProgressText';

export default ScreenReaderText;
export {
  LiveAnnouncement,
  DescriptiveText,
  InstructionalText,
  StatusText,
  ProgressText,
  useScreenReaderAnnouncements
};
